// src/components/Combobox.tsx
import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/app/global-components/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/app/global-components/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/global-components/popover";
import { LANG } from "@/types";
import { useLocale } from "@/middleware";

interface Option {
  value: string;
  label: string;
}

interface ComboboxProps {
  options: Option[];
  value: string | null | number;
  onChange: (value: string | null) => void;
  placeholder?: string;
}

export function ComboboxView({
  options,
  value,
  onChange,
  placeholder,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const { locale } = useLocale();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="justify-between relative w-full"
        >
          {value !== null
            ? options.find((option) => option.value === value)?.label
            : placeholder}
          <ChevronsUpDown
            className={cn(
              "ml-2 h-4 w-4 absolute shrink-0 opacity-50",
              locale === LANG.AR ? "left-2" : "right-2"
            )}
          />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-0 bg-primary-gray w-full xl:w-[380px]"
      >
        <Command>
          <CommandList>
            <CommandEmpty>No {placeholder?.toLowerCase()} found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={(currentValue) => {
                    onChange(currentValue === value ? null : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
